import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "../../store";
import { useHomeData, HeroVideo } from "../../homeData";
import { LearnSection, InstagramSection, BlogSection } from "./V1";
import { ArrowRight, Wind, Battery, Cpu, Zap } from "lucide-react";

/* V3 — Adventure Lifestyle. Vibrant bento, playful, gradient accents. */
export default function V3() {
  const { t } = useApp();
  const { products, blog, igPosts } = useHomeData();

  return (
    <main className="bg-white" data-testid="home-v3">
      {/* HERO with rounded corners */}
      <section className="relative pt-20 px-4 md:px-6">
        <div className="relative h-[88vh] rounded-3xl overflow-hidden">
          <HeroVideo overlay="linear-gradient(180deg, rgba(2,132,199,0.25) 0%, rgba(15,23,42,0.45) 100%)" />
          <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-16 text-white">
            <div className="overline mb-4 text-cyan-300">{t.hero.overline}</div>
            <h1 className="font-serif-display text-6xl md:text-8xl lg:text-9xl leading-[0.92]">{t.hero.title}</h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mt-6">{t.hero.subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/products/efoils" className="btn-primary">{t.hero.cta1}</Link>
              <Link to="/book" className="btn-soft">{t.hero.cta2}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* USPs Bento */}
      <section className="py-28 px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="overline mb-3">{t.usps.overline}</div>
        <h2 className="font-serif-display text-5xl md:text-7xl text-slate-900 mb-14">{t.usps.title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          <Tile span="md:col-span-7" highlight>
            <Zap size={28} className="text-white" />
            <div className="font-serif-display text-7xl md:text-9xl text-white leading-none">{t.usps.f1t}</div>
            <div className="text-white/90 mt-3 max-w-md">{t.usps.f1d}</div>
          </Tile>
          <Tile span="md:col-span-5" img="https://images.unsplash.com/photo-1769816325482-59769ff9ab37?crop=entropy&cs=srgb&fm=jpg&q=85&w=900">
            <Battery size={28} className="text-white" />
            <div className="font-serif-display text-4xl text-white">{t.usps.f2t}</div>
            <div className="text-white/85 text-sm mt-2">{t.usps.f2d}</div>
          </Tile>
          <Tile span="md:col-span-5" img="https://images.unsplash.com/photo-1763075958748-9a0c9c8799c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=900">
            <Cpu size={28} className="text-white" />
            <div className="font-serif-display text-4xl text-white">{t.usps.f3t}</div>
            <div className="text-white/85 text-sm mt-2">{t.usps.f3d}</div>
          </Tile>
          <Tile span="md:col-span-7" tealBg>
            <Wind size={28} className="text-white" />
            <div className="font-serif-display text-5xl md:text-6xl text-white">Endless rides</div>
            <div className="text-white/90 mt-3 max-w-lg">From sunrise to sunset. Six-second swaps mean you ride the conditions, not the clock.</div>
          </Tile>
        </div>
      </section>

      {/* Product showcase */}
      <section className="py-20 px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-serif-display text-5xl md:text-6xl text-slate-900">{t.shop.title}</h2>
          <Link to="/products/efoils" className="hidden md:flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-ui text-slate-700 hover:text-sky-500">{t.shop.viewAll} <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {products.slice(0, 3).map((p, i) => (
            <Link to={`/product/${p.slug}`} key={p.id} className="group rounded-2xl overflow-hidden bg-white border border-slate-200 hover:border-sky-400 transition-all hover:-translate-y-1 hover:shadow-2xl">
              <div className="aspect-[4/5] overflow-hidden bg-gradient-to-br from-sky-50 to-cyan-50">
                <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <div className="overline">{p.category}</div>
                <div className="font-serif-display text-2xl text-slate-900 mt-2">{p.name}</div>
                <div className="mt-3 flex items-end justify-between">
                  <div className="font-serif-display text-2xl text-sky-600">${p.base_price?.toLocaleString()}</div>
                  <div className="text-xs uppercase tracking-[0.18em] font-ui text-sky-500">Configure →</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <LearnSection t={t} />
      <InstagramSection posts={igPosts} />
      <BlogSection blog={blog} />
    </main>
  );
}

function Tile({ children, span = "", highlight, tealBg, img }) {
  const bg = highlight
    ? "bg-gradient-to-br from-sky-500 to-cyan-500"
    : tealBg
    ? "bg-gradient-to-br from-teal-500 to-sky-600"
    : img
    ? ""
    : "bg-slate-900";
  return (
    <div className={`relative ${span} rounded-2xl overflow-hidden p-8 md:p-10 min-h-[220px] flex flex-col justify-end gap-2 ${bg}`}>
      {img && (<>
        <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 to-slate-900/30" />
      </>)}
      <div className="relative">{children}</div>
    </div>
  );
}
